import { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useSubmitPromoCode } from './useSubmitPromoOrGiftCardCode';

/**
 * Attempts to submit the promo code from the deep link.
 */
export const usePromoCodeDeepLink = (promoCode?: string) => {
  const isLoggedIn = useIsLoggedIn();
  const isEnabled = useFeatureFlag('CELS-4290-promo-code-deep-link-enabled');
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();
  const { submitCode } = useSubmitPromoCode();
  const { track } = useTelemetry();

  const handleSubmitPromoCode = useCallback(async () => {
    if (!isLoggedIn || !isEnabled || !promoCode) return;

    const { isSubmitSuccess, isAlreadyApplied } = await submitCode(promoCode);

    if (isSubmitSuccess) {
      track('promo_code.deep_link.success');
      addNoticeBanner({
        palette: 'success',
        text: formatMessage(messages.promoCodeApplied),
      });

      return;
    }

    if (isAlreadyApplied) {
      track('promo_code.deep_link.already_applied');
      addNoticeBanner({
        palette: 'caution',
        text: formatMessage(messages.promoCodeAlreadyApplied),
      });

      return;
    }

    track('promo_code.deep_link.failed');
    addNoticeBanner({
      palette: 'caution',
      text: formatMessage(messages.invalidPromoCode),
    });
  }, [
    isLoggedIn,
    isEnabled,
    promoCode,
    submitCode,
    track,
    addNoticeBanner,
    formatMessage,
  ]);

  useEffect(() => {
    void handleSubmitPromoCode();
  }, [handleSubmitPromoCode]);
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  promoCodeApplied: {
    defaultMessage:
      'Promo code was successfully redeemed. A new reward has been added to your account.',
    description: 'Bag | Promo code | Promo code applied',
  },
  promoCodeAlreadyApplied: {
    defaultMessage: 'The promo code you entered has already been redeemed.',
    description: 'Bag | Promo code | Promo code already applied',
  },
  invalidPromoCode: {
    defaultMessage: 'Your promo code could not be added, it is invalid.',
    description: 'Bag | Promo code | Invalid promo code',
  },
});
